import React, { Fragment, useState, useCallback } from "react";
import { Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";

export type ToastType = "success" | "error" | "warning" | "info";
export type ToastLocation =
  | "top-right"
  | "top-left"
  | "bottom-right"
  | "bottom-left"
  | "top-center"
  | "bottom-center";

export interface ToastMessage {
  id?: string;
  message: string;
  type?: ToastType;
  duration?: number;
  location?: ToastLocation;
  icon?: React.ReactNode;
}

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastContext = React.createContext<{
  addToast: (message: Omit<ToastMessage, "id">) => void;
}>({
  addToast: () => {},
});

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((toast: Omit<ToastMessage, "id">) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = {
      ...toast,
      id,
      location: toast.location || "top-right",
    };
    setToasts((prev) => [...prev, newToast]);

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, toast.duration || 3000);
  }, []);

  const toastsByLocation = toasts.reduce((acc, toast) => {
    const location = toast.location || "top-right";
    if (!acc[location]) {
      acc[location] = [];
    }
    acc[location].push(toast);
    return acc;
  }, {} as Record<ToastLocation, ToastMessage[]>);

  const locationClasses = {
    "top-right": "top-2 sm:top-4 right-2 sm:right-4",
    "top-left": "top-2 sm:top-4 left-2 sm:left-4",
    "bottom-right": "bottom-2 sm:bottom-4 right-2 sm:right-4",
    "bottom-left": "bottom-2 sm:bottom-4 left-2 sm:left-4",
    "top-center":
      "top-2 sm:top-4 left-2 right-2 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2",
    "bottom-center":
      "bottom-2 sm:bottom-4 left-2 right-2 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2",
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {Object.entries(locationClasses).map(([location, positionClasses]) => (
        <div
          key={location}
          className={`fixed ${positionClasses} z-50 space-y-2 sm:space-y-3 max-w-sm sm:max-w-md`}
        >
          {toastsByLocation[location as ToastLocation]?.map((toast) => (
            <Toast
              key={toast.id}
              {...toast}
              onClose={() =>
                setToasts((prev) => prev.filter((t) => t.id !== toast.id))
              }
            />
          ))}
        </div>
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

interface ToastProps extends ToastMessage {
  onClose: () => void;
}

const defaultIcons = {
  success: (
    <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
  ),
  error: <XCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />,
  warning: (
    <ExclamationTriangleIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
  ),
  info: (
    <InformationCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
  ),
};

const Toast: React.FC<ToastProps> = ({
  message,
  type = "info",
  onClose,
  icon,
}) => {
  const typeStyles = {
    success: "bg-green-500 text-white",
    error: "bg-red-500 text-white",
    warning: "bg-yellow-500 text-black",
    info: "bg-blue-500 text-white",
  };

  const toastIcon = icon || defaultIcons[type];

  return (
    <Transition
      appear
      show={true}
      as={Fragment}
      enter="transition ease-out duration-300"
      enterFrom="opacity-0 translate-x-full sm:translate-x-full translate-x-4"
      enterTo="opacity-100 translate-x-0"
      leave="transition ease-in duration-200"
      leaveFrom="opacity-100 translate-x-0"
      leaveTo="opacity-0 translate-x-full sm:translate-x-full translate-x-4"
    >
      <div
        className={`
          ${typeStyles[type]} 
          px-3 py-2 sm:px-4 sm:py-3 rounded-lg shadow-lg
          relative flex items-start justify-between gap-2
          text-sm sm:text-base
          min-w-0 w-full
        `}
      >
        <div className="flex items-start gap-2 min-w-0 flex-1">
          {toastIcon}
          <span className="break-words leading-relaxed">{message}</span>
        </div>
        <button
          onClick={onClose}
          className="ml-2 text-white hover:opacity-75 flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-full hover:bg-black hover:bg-opacity-20 transition-colors"
          aria-label="Cerrar notificación"
        >
          <span className="text-lg sm:text-xl leading-none">×</span>
        </button>
      </div>
    </Transition>
  );
};

export default Toast;
